import {
	Edit as EditIcon,
	Visibility as VisibilityIcon,
	Add as AddIcon,
	Shortcut as ShortcutIcon,
	Delete as DeleteIcon,
	Refresh as RefreshIcon,
} from '@mui/icons-material'
import CloseIcon from '@mui/icons-material/Close'
import {
	Box,
	Button,
	CircularProgress,
	Dialog,
	DialogContent,
	DialogTitle,
	IconButton,
	Tooltip,
} from '@mui/material'
import { lighten } from '@mui/material/styles'
import { makeStyles } from '@mui/styles'
import {
	DataGridPremium,
	GridActionsCellItem,
	GridFooterContainer,
	GridToolbarContainer,
	gridPaginatedVisibleSortedGridRowEntriesSelector,
	useGridApiRef,
} from '@mui/x-data-grid-premium'
import ToastAlert from 'components/core/Layouts/ToastAlert'
import { loadForm } from 'components/data/dataServices'
import { cloneDeep, debounce, isEmpty, isEqual } from 'lodash'
import { useCallback, useEffect, useMemo, useState, useReducer } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector, useDispatch } from 'react-redux'
import moment from 'moment'
import {
	getColorWithoutId,
	hasUpdatingPermission,
	isNumber,
	getAggregationModel,
	getRowGroupingModel,
	DEFAULT_LOGIC_OPERATOR,
	hasDeletePermission,
} from 'utils/functions/helpers'
import {
	getDefaultOperator,
	getChildLinkedFieldsByTileKey,
} from '../../../../../utils/functions/helpers'
import DoformsRecordsActions from '../../../../data/datagrid/DoformsRecordsActions'
import ExportButton from '../../../../data/datagrid/ExportButton'
import { useTileDashboard } from '../../dashboard/Dashboard'
import { compareValueWithPrimaryColumn } from '../../../dis-v2/menu-items/_components/ConditionalColorPickerDialog/utils'
import { checkKeyIsExist } from '../utils/helpers'
import { LoadingButton } from '@mui/lab'
import { getFilteredRows } from '../../../../data/dataHelpers'
import DoFormsFormTile from '../doforms-form/DoformsFormTile'
import useTileQuery from '../../hooks/useTileQuery'
import { DEFAULT_CHECKED_OPTIONS } from 'components/pages/dis-v2/_components/OtherOptions'
import { DEFAULT_DOFORM_CHECKED_OPTIONS } from '../doforms-form/DoformsForm'
import {
	calculateUserTimezoneOffset,
	dayjsToMoment,
	isDayjsObject,
} from 'utils/functions/doformsDateUtil'
import MasterDateTimeForm from '../master-datetime/MasterDateTimeForm'
import DoformsDataImportView from 'components/data/datagrid/DoformsDataImportView'
import { ENV_ACTIONS } from 'reducers/environmentReducer'
import DatagridSendDispatch from './DatagridSendDispatch'
import {
	SUPPORT_IMPORTED_VIEW_TYPES,
	NOT_SUPPORT_DELETE_ACTION_RECORD_TYPES,
} from '../../../../data/datagrid/CreatViewComponents/ViewDialogUtils'

import { useModalDashboardDialog } from '../modal-dashboard/ModalDashboard'

const getBackgroundColor = (color) => lighten(color, 0)

const getHoverBackgroundColor = (color) => lighten(color, 0.2)

const getSelectedBackgroundColor = (color) => lighten(color, 0.3)

const getSelectedHoverBackgroundColor = (color) => lighten(color, 0.2)

const useStyles = makeStyles(() => ({
	gridFooterActions: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		height: '100%',
		width: '100%',
		paddingLeft: '16px',
		paddingRight: '16px',
	},
	cancelLoadingIcon: (props) => ({
		color: props.color,
		'&:hover': {
			color: props.active.color,
			backgroundColor: 'transparent',
		},
	}),

	dialog: {
		'& .MuiButton-root': {
			textTransform: 'none !important',
		},
	},
	dialogTitle: {
		display: 'flex',
		justifyContent: 'space-between',
		alignItems: 'center',
		textAlign: 'center',
	},

	icon: (props) => ({
		color: props.color,
		'&:hover': {
			color: props.active.color,
			backgroundColor: 'transparent',
		},
	}),
}))

const DATA_FILTERS_REDUCER = {
	SERVER: 'server',
	CLIENT: 'client',
	LINKED_FIELDS: 'linked',
	CHILD_MODAL_DASHBOARD: 'child',
}

function dataFiltersReducer(state, action) {
	switch (action.type) {
		case DATA_FILTERS_REDUCER.CLIENT: {
			if (isEqual(state.clientFilters, action.value)) return state
			const filtersToSave = (action.value || []).map((item) => ({
				...item,
				filterType: action.type,
			}))
			return {
				...state,
				clientFilters: filtersToSave || [],
			}
		}
		case DATA_FILTERS_REDUCER.LINKED_FIELDS: {
			if (isEqual(state.linkedFilters, action.value)) return state
			const filtersToSave = (action.value || []).map((item) => ({
				...item,
				filterType: action.type,
			}))
			return {
				...state,
				linkedFilters: filtersToSave || [],
			}
		}
		case DATA_FILTERS_REDUCER.SERVER: {
			if (isEqual(state.serverFilters, action.value)) return state
			const filtersToSave = (action.value || []).map((item) => ({
				...item,
				filterType: action.type,
			}))
			return {
				...state,
				serverFilters: filtersToSave || [],
			}
		}
		case DATA_FILTERS_REDUCER.CHILD_MODAL_DASHBOARD: {
			if (isEqual(state.childFilters, action.value)) return state
			const filtersToSave = (action.value || []).map((item) => ({
				...item,
				childFilters: action.type,
			}))
			return {
				...state,
				childFilters: filtersToSave || [],
			}
		}

		default:
			return state
	}
}

function DataTable({
	tile,
	columns,
	defaultRows,
	rows,
	viewData = {},
	tab,
	haveExport = false,
	haveRefresh = false,
	haveImport = false,
	allowEdit = false,
	allowDelete = false,
	allowDispatch = false,
	columnsChecked,
	records,
	setCurrentRecords,
	serverFilterModel,
	clientFilterModel,
	aggregationModel,
	rowGroupingModel,
	sortModel,
	serverSortModel,
	hasMoreRecords,
	isShowHeaders,
	isShowTotalRows,
	bgColorRowCondition = {},
	onHeaderFilterModel,
	onCancelLoadingRecords,
	columnsWidth,
	setColumnsWidth,
	settings,
	dashboardKey,
	fetchDataFrmDoForms,
	refetchRecords,
	filterConfigs,
	viewType,
	printTitle,
	dynamicRowHeight = false,
	recordsLoading,
	parentPassedIdValue,
	parentModalDashboardSetting,
	mainModalDashboardSetting,
}) {
	const [t] = useTranslation('common')
	const { environment } = useSelector((state) => state)
	const permissions = environment?.userCurrent?.rights
	const iconTheme = environment.theme.icons
	const classes = useStyles(iconTheme)
	const apiRef = useGridApiRef()
	const dispatch = useDispatch()
	const userOffset = useMemo(() => {
		return calculateUserTimezoneOffset(environment)
	}, [environment])

	const tileName = tile?.i ?? ''
	const editedSettings = JSON.parse(tile?.settings)

	const { onSelectRow, selectedFields, onSelectCurrent } = useTileDashboard()
	const { handleOpenModalDashboardDialog, handleCloseModalDashboardDialog, ModalDashboard } =
		useModalDashboardDialog({ parentPassedIdValue, parentModalDashboardSetting })

	const [selectionModel, setSelectionModel] = useState([])
	const [action, setAction] = useState(null)
	const [actionRecord, setActionRecord] = useState(null)
	const [open, setOpen] = useState(false)
	const [error, setError] = useState(null)
	const [formSelected, setFormSelected] = useState(null)
	const [showNewBtn, setShowNewBtn] = useState(false)
	const [isOpenDialog, setIsOpenDialog] = useState(false)
	const [itemTile, setItemTile] = useState({})
	const [titleLabel, setTitleLabel] = useState('')
	const [openSendDispatch, setOpenSendDispatch] = useState(false)
	let TileComponent = DoFormsFormTile

	const tileComponents = {
		// datagrid: DatagridTile,
		// GeoMap: MapTile,
		// Page: PageTile,
		// Chart: ChartTile,
		// FormList: DatagridTile,
		// ImageViewer: ImageViewerTile,
		// MobileUnits: MobileUnitsTile,
		// Feed: FeedTile,
		// DoformsMap: DoformsMapTile,
		// Browser: BrowserTile,
		// TextSearch: TextSearchTile,
		DoFormsForm: DoFormsFormTile,
	}

	const [alertInfo, setAlertInfo] = useState({
		open: false,
		text: '',
		type: 'success',
	})

	const [dataFilters, dispatchDataFilters] = useReducer(dataFiltersReducer, {
		serverFilters: [],
		linkedFilters: [],
		clientFilters: [],
		childFilters: [],
	})

	// grid models
	const [gridFilterModel, setGridFilterModel] = useState({ items: [] })
	const [gridRowGroupingModel, setGridRowGroupingModel] = useState([])
	const [gridSortModel, setGridSortModel] = useState([])
	const [gridAggregationModel, setGridAggregationModel] = useState({})
	const [gridColumnVisibilityModel, setGridColumnVisibilityModel] = useState({})
	const [isHideRemoveAllFilters, setIsHideRemoveAllFilters] = useState(true)
	const [isOpenFrmDoFrm, setIsOpenFrmDoFrm] = useState(false)
	const [otherOptionsCheckedForDoForms, setOtherOptionsCheckedForDoForms] = useState([])

	useEffect(() => {
		if (!isEmpty(settings.connectedDoFormsKey)) {
			let item = tileList?.find((object) => object.key === settings.connectedDoFormsKey)
			if (item) {
				setShowNewBtn(true)
				return
			}
		}
		setShowNewBtn(false)
	}, [settings?.connectedDoFormsKey])

	useEffect(() => {
		if (isOpenFrmDoFrm === true && isOpenDialog === false) {
			if (checkKeyIsExist('refreshOnSubmit', otherOptionsCheckedForDoForms) === true) {
				//refresh data
				fetchDataFrmDoForms()
				setIsOpenFrmDoFrm(false)
			}
		}
	}, [isOpenDialog])

	const { tileList, isLoading } = useTileQuery({
		dashboardKey,
		enableConfig: true,
	})

	const doformsTile = useMemo(() => {
		const currentList =
			tileList?.length > 0 ? tileList?.filter((tile) => tile?.type === 'DoFormsForm') : []
		return currentList
	}, [tileList])

	const bgColorRowStyles = useMemo(() => {
		const result = {}
		Object.keys(bgColorRowCondition).forEach((color) => {
			if (color === 'primaryColumn') return
			const colorWithoutId = getColorWithoutId(color)
			const newColor = colorWithoutId ? colorWithoutId.substring(1) : ''

			result[`& .app-row-datagrid-${newColor}`] = {
				backgroundColor: getBackgroundColor(`${colorWithoutId}`),
				'&:hover': {
					backgroundColor: getHoverBackgroundColor(`${colorWithoutId}`),
				},
				'&.Mui-selected': {
					backgroundColor: getSelectedBackgroundColor(`${colorWithoutId}`),
					boxShadow: 'inset 0.5px 0.5px 0px 3px',
					'&:hover': {
						backgroundColor: getSelectedHoverBackgroundColor(`${colorWithoutId}`),
					},
				},
			}
		})
		return result
	}, [bgColorRowCondition])

	useEffect(() => {
		const filterFromOtherDataGrid = [...filterConfigs]
		if (isEqual(filterFromOtherDataGrid, dataFilters.linkedFilters)) return
		const computedFilterFromOtherDataGrid = filterFromOtherDataGrid.map((item) => {
			if (item.keyData && ['Master_DateTime_Start', 'Master_DateTime_End'].includes(item.keyData)) {
				const utcMoment = item.value
				const userMoment = utcMoment?.utcOffset(environment.userCurrent.time.timezone.offset || 0)
				return { ...item, value: userMoment }
			}

			return item
		})
		dispatchDataFilters({
			type: DATA_FILTERS_REDUCER.LINKED_FIELDS,
			value: computedFilterFromOtherDataGrid || [],
		})
	}, [filterConfigs])

	useEffect(() => {
		const newValue = serverFilterModel?.items
			? serverFilterModel?.items?.filter((item) => !item.columnField)
			: serverFilterModel?.filter((item) => !item.columnField)

		if (isEqual(newValue, dataFilters.serverFilters)) return
		dispatchDataFilters({ type: DATA_FILTERS_REDUCER.SERVER, value: newValue || [] })
	}, [serverFilterModel])

	useEffect(() => {
		const newValue = clientFilterModel?.items
			? clientFilterModel?.items?.filter((item) => !item.columnField)
			: clientFilterModel?.filter((item) => !item.columnField)

		if (isEqual(newValue, dataFilters.clientFilters)) return
		dispatchDataFilters({ type: DATA_FILTERS_REDUCER.CLIENT, value: newValue || [] })
	}, [clientFilterModel])

	useEffect(() => {
		const initGridVisibleModel = columns.reduce((acc, column) => {
			if (columnsChecked.includes(column.field)) {
				acc[column.field] = true
			} else {
				acc[column.field] = false
			}
			return acc
		}, {})

		setGridColumnVisibilityModel(initGridVisibleModel)
	}, [columnsChecked, columns])

	useEffect(() => {
		if (!isEmpty(serverSortModel)) {
			setGridSortModel(serverSortModel)
			return
		}

		setGridSortModel(sortModel)
	}, [sortModel, serverSortModel])

	useEffect(() => {
		const serverAggregationModel = getAggregationModel(columns)
		if (isEmpty(serverAggregationModel)) {
			setGridAggregationModel(aggregationModel)
			return
		}

		setGridAggregationModel(serverAggregationModel)
	}, [columns, aggregationModel])

	useEffect(() => {
		const serverRowGroupingModel = getRowGroupingModel(columns)

		if (isEmpty(serverRowGroupingModel)) {
			setGridRowGroupingModel(rowGroupingModel)
			return
		}

		setGridRowGroupingModel(serverRowGroupingModel)
	}, [columns, rowGroupingModel])

	const formattedRowsWithNumbers = useMemo(() => {
		if (!rows.length) return []
		const filteredRows = getFilteredRows(
			{ items: [...dataFilters?.serverFilters, ...dataFilters?.childFilters] },
			rows
		)
		return filteredRows.map((row) => {
			// loop through property of row and find data with number type
			// then format it to have number
			const rowWithFormatValue = {}
			Object.keys(row).forEach((key) => {
				if (moment.isMoment(row[key])) {
					rowWithFormatValue[key] = row[key] || undefined
				} else if (isNumber(row[key])) {
					rowWithFormatValue[key] = Number(row[key])
				} else {
					rowWithFormatValue[key] = row[key] || undefined
				}
			})
			return rowWithFormatValue
		})
	}, [dataFilters?.serverFilters, dataFilters?.childFilters, rows])

	const getRowBackgroundColor = (params) => {
		const primaryColumnName = bgColorRowCondition['primaryColumn']
		const primaryColumnDetail = columns.find((col) => col.field === primaryColumnName)
		if (!primaryColumnDetail) return null

		for (const color in bgColorRowCondition) {
			if (color === 'primaryColumn') continue
			if (bgColorRowCondition.hasOwnProperty(color)) {
				const conditions = bgColorRowCondition[color]
				const isAllConditionsMatched = conditions.every((condition) => {
					return compareValueWithPrimaryColumn(
						primaryColumnDetail,
						condition,
						params,
						userOffset,
						t
					)
				})
				if (isAllConditionsMatched) {
					const colorWithoutId = getColorWithoutId(color)
					return colorWithoutId
				}
			}
		}
		return null
	}

	const handleAction = useCallback(
		(params, action, records, viewData) => async () => {
			try {
				let record = records[params.id - 1]
				record = {
					...record,
					key:
						record.sourceType === 'SUBMISSION' || record.sourceType === 'ACTIVITY'
							? record.submissionKey
							: record.dispatchKey,
				}

				if (
					(['edit'].includes(action) || ['view'].includes(action) || ['delete'].includes(action)) &&
					('activityKey' in record || 'key' in record)
				) {
					/*var key = record.key ? record.key : record.activityKey;
					const response = await getFormSelectedFromActivityKey(
						record.key,
						environment.apiToken
					)

					const currentFormSelected = response.data;
					if (Object.keys(currentFormSelected).length > 0) {
						switch (tab) {
							case 'views':
								dispatch({
									type: VIEWS_ACTIONS.UPDATE_VIEW_SELECTED_KEY,
									payload: currentFormSelected.formKey,
								})
								break
							case 'devices':
								dispatch({
									type: DEVICES_ACTIONS.FORM_SELECTED,
									payload: currentFormSelected,
								})
								break
							case 'activity':
								dispatch({
									type: ACTIVITY_ACTIONS.FORM_SELECTED,
									payload: currentFormSelected,
								})
								break
							default:
								break
						}
						
					}*/
					setAction(action)
					setActionRecord(record)
					setOpen(action !== 'images' ? true : false)
				}
			} catch (err) {
				if (err?.response?.data?.code === 404) {
					setError(`${t('common:misc.record')} ${t('common:misc.hasBeenDeleted')}`)
				} else {
					setError('Code ' + err?.response?.data?.code + ': ' + err?.response?.data?.message)
				}
			}
		},
		[action, records]
	)

	const handleClose = () => {
		setAction(null)
		setActionRecord(null)
		setOpen(false)
	}

	const actionField = (allowEdit, allowDelete) => {
		return {
			field: 'actions',
			type: 'actions',
			width: 75,
			disableReorder: true,
			getActions: (params) => {
				const loading = [
					<Box sx={{ pl: 1.4, position: 'relative', top: '3px', left: '-6px' }}>
						<CircularProgress color="inherit" size={17} />
					</Box>,
				]

				let popupActions = [
					<GridActionsCellItem
						key={params.id}
						icon={<VisibilityIcon />}
						label={t('common:misc.view')}
						onClick={handleAction(params, 'view', records, viewData)}
						showInMenu
					/>,
				]
				if (allowEdit && permissions && hasUpdatingPermission(permissions)) {
					popupActions = [
						...popupActions,
						<GridActionsCellItem
							key={params.id}
							icon={<EditIcon />}
							label={t('common:misc.edit')}
							onClick={handleAction(params, 'edit', records, viewData)}
							showInMenu
						/>,
					]
				}
				if (
					!environment.isReadOnlySubmission &&
					allowDelete &&
					permissions &&
					hasDeletePermission(permissions)
				) {
					if (
						records &&
						!NOT_SUPPORT_DELETE_ACTION_RECORD_TYPES.includes(records[params.id - 1]?.type)
					) {
						popupActions = [
							...popupActions,
							<GridActionsCellItem
								key={params.id}
								icon={<DeleteIcon />}
								label={t('common:misc.delete')}
								onClick={handleAction(params, 'delete', records, viewData)}
								showInMenu
							/>,
						]
					}
				}

				const allActions = [
					...(params?.row?.isLoading ? loading : []),
					...(!params?.row?.isLoading ? popupActions : []),
				]
				const AUTO_GENERATED_FOOTER_FROM_AGGREGATION = 'auto-generated-group-footer-root'

				// Remove the action menu icon from the aggregate row
				if (params?.id === AUTO_GENERATED_FOOTER_FROM_AGGREGATION) {
					return []
				}

				return allActions
			},
		}
	}

	useEffect(() => {
		if (editedSettings.formKey) {
			loadForm(editedSettings.formKey, environment.apiToken)
				.then((response) => {
					const form = response.data
					if (editedSettings?.projectKey) {
						form.projectKey = editedSettings.projectKey
					}
					setFormSelected(form)
				})
				.catch((err) => {
					console.log(err)
				})
				.finally(() => {})
		}
	}, [editedSettings.formKey])

	useEffect(() => {
		// filters of sever and linked fields
		const allFilters = [...dataFilters?.linkedFilters]

		// get unique filter with lodash
		const uniqueFilterWithId = allFilters.map((item, index) => ({
			...item,
			id: `${index}_${item.type || DATA_FILTERS_REDUCER.CLIENT}_${item.field}_${item.value}`,
		}))

		setIsHideRemoveAllFilters(!isEmpty(dataFilters?.linkedFilters))
		if (isEqual(uniqueFilterWithId, gridFilterModel.items)) return

		setGridFilterModel({ items: uniqueFilterWithId })
	}, [JSON.stringify(dataFilters?.linkedFilters)])

	const neededColumns = useMemo(() => {
		var actionFields = allowEdit || allowDelete ? [actionField(allowEdit, allowDelete)] : null
		var columnFiltered = columns.filter(Boolean).map((column) => {
			if (!column.field) return column
			const width = columnsWidth[column.field]
			if (!!width) {
				column.width = width
			}

			switch (column.field) {
				case 'Description':
					column.title = t('common:misc.description')
					column.headerName = t('common:misc.description')
					break
				case '@StartTime':
					column.title = t('common:misc.startTime')
					column.headerName = t('common:misc.startTime')
					break
				case '@SourceName':
					column.title = t('common:misc.sourceName')
					column.headerName = t('common:misc.sourceName')
					break
				case '@OwnerName':
					column.title = t('common:misc.ownerName')
					column.headerName = t('common:misc.ownerName')
					break
				case '@UserStatus':
					column.title = t('common:misc.userStatus')
					column.headerName = t('common:misc.userStatus')
					break
				case '@StatusTime':
					column.title = t('common:misc.statusTime')
					column.headerName = t('common:misc.statusTime')
					break
				default:
					break
			}
			// Disable aggregation for non-number columns
			if (column.type !== 'number') {
				return {
					...column,
					aggregable: false,
				}
			}

			return column
		})
		if (actionFields) {
			return [...actionFields, ...columnFiltered]
		}
		return [...columnFiltered]
	}, [columns, columnsWidth, columnsChecked, t, handleAction])

	const hasCustomToolbar = useMemo(
		() => haveExport || haveImport || showNewBtn || allowDispatch || haveRefresh,
		[haveExport, haveImport, showNewBtn, allowDispatch, haveRefresh]
	)

	useEffect(() => {
		if (isEmpty(parentModalDashboardSetting)) return
		const linkedFields = getChildLinkedFieldsByTileKey(tile?.key, parentModalDashboardSetting) || []

		const childLinkedFilters = linkedFields
			?.map((field, index) => {
				const column = columns.find((col) => col.field === field)
				if (!column) return null

				const defaultOperator = getDefaultOperator(column.orgType, true)
				return {
					field,
					operator: parentPassedIdValue === '' ? 'isEmpty' : defaultOperator,
					value: parentPassedIdValue,
					type: column.type,
				}
			})
			.filter((item) => !!item)

		dispatchDataFilters({
			type: DATA_FILTERS_REDUCER.CHILD_MODAL_DASHBOARD,
			value: childLinkedFilters || [],
		})
	}, [parentPassedIdValue, parentModalDashboardSetting, columns, tile])

	const CustomFooter = () => {
		const totalValue = isEmpty(gridFilterModel)
			? apiRef?.current?.state?.sorting?.sortedRows?.length
			: apiRef?.current?.state?.rowsMeta?.positions?.length
		return (
			<GridFooterContainer>
				<div className={classes.gridFooterActions}>
					{hasMoreRecords ? (
						<Button
							id="cancel-loading-records"
							className={classes.cancelLoadingIcon}
							startIcon={<CircularProgress color="inherit" size={16} />}
							loadingPosition="start"
							variant={'text'}
							sx={{
								textTransform: 'none',
							}}
							onClick={onCancelLoadingRecords}
						>
							{t('common:misc.cancelLoadingRecords')}
						</Button>
					) : (
						<div></div>
					)}

					<div>
						Total rows: {totalValue}
						{/* {apiRef?.current?.state?.rowsMeta?.positions?.length ||
							apiRef?.current?.state?.sorting?.sortedRows?.length} */}
					</div>
				</div>
			</GridFooterContainer>
		)
	}

	const handleOpen = (e) => {
		e.preventDefault()
		if (tileList?.length > 0 && showNewBtn === true) {
			let item = tileList?.find((object) => object.key === settings.connectedDoFormsKey)
			if (item) {
				TileComponent = tileComponents[item?.type]
				setIsOpenDialog(true)
				setIsOpenFrmDoFrm(true)
				item.isShowDialog = true
				setItemTile(item)
				const parsedSettings = JSON.parse(item?.settings)
				if (isEqual(parsedSettings.otherOptionsChecked, DEFAULT_CHECKED_OPTIONS)) {
					parsedSettings.otherOptionsChecked = DEFAULT_DOFORM_CHECKED_OPTIONS
				}
				setOtherOptionsCheckedForDoForms(parsedSettings.otherOptionsChecked)

				if (checkKeyIsExist('showTileLabel', parsedSettings.otherOptionsChecked)) {
					setTitleLabel(item.name)
				} else {
					setTitleLabel('  ')
				}
			}
		}
	}
	const handleCloseDialog = (isClickOnIcon) => {
		if (isClickOnIcon === true) {
			setIsOpenFrmDoFrm(false)
		}
		setIsOpenDialog(false)
		if (tileList?.length > 0 && showNewBtn === true) {
			let item = tileList?.find((object) => object.key === settings.connectedDoFormsKey)
			TileComponent = tileComponents[item?.type]
			item.isShowDialog = false
		}
	}

	const handleOpenDispatch = (e) => {
		dispatch({
			type: ENV_ACTIONS.SHOW_SEND_DISPATCH,
			payload: 'dashboard',
		})
	}

	const handleFilterModelChange = debounce((nextFilterModel, detail) => {
		// Check if user clicked on filter icon, don't do anything
		if (!detail?.reason) {
			setGridFilterModel(nextFilterModel)
			return
		}

		// if (detail?.reason === 'removeAllFilterItems') {
		// 	if (!isEmpty(dataFilters.serverFilters) || !isEmpty(dataFilters.linkedFilters)) {
		// 		setAlertInfo((prev) => ({
		// 			...prev,
		// 			open: true,
		// 			type: 'error',
		// 			text: "You don't have permission to change filter",
		// 		}))
		// 		return
		// 	}

		// 	onHeaderFilterModel('filterModel', [])
		// 	setGridFilterModel(nextFilterModel)
		// 	return
		// }

		// if (detail?.reason === 'changeLogicOperator') {
		// 	if (!isEmpty(dataFilters.serverFilters) || !isEmpty(dataFilters.linkedFilters)) {
		// 		setAlertInfo((prev) => ({
		// 			...prev,
		// 			open: true,
		// 			type: 'error',
		// 			text: "You don't have permission to change filter",
		// 		}))
		// 		return
		// 	}
		// }

		// const nextServerFilters = nextFilterModel.items.filter(
		// 	(item) => DATA_FILTERS_REDUCER.SERVER === item.filterType
		// )
		// const nextLinkedFilters = nextFilterModel.items.filter(
		// 	(item) => DATA_FILTERS_REDUCER.LINKED_FIELDS === item.filterType
		// )

		// const isRemovingStableFilter =
		// 	nextServerFilters.length !== dataFilters.serverFilters.length ||
		// 	nextLinkedFilters.length !== dataFilters.linkedFilters.length
		// const isEditingServerFilters = !isEqual(
		// 	dataFilters.serverFilters.map((item) => ({
		// 		value: item.value,
		// 		operator: item.operator,
		// 		field: item.field,
		// 	})),
		// 	nextServerFilters.map((item) => ({
		// 		value: item.value,
		// 		operator: item.operator,
		// 		field: item.field,
		// 	}))
		// )

		// const isEditingLinkedFilters = !isEqual(
		// 	dataFilters.linkedFilters.map((item) => ({
		// 		value: item.value,
		// 		operator: item.operator,
		// 		field: item.field,
		// 	})),
		// 	nextLinkedFilters.map((item) => ({
		// 		value: item.value,
		// 		operator: item.operator,
		// 		field: item.field,
		// 	}))
		// )

		// const isEditingStableFilter = isEditingServerFilters || isEditingLinkedFilters
		// const isCustomizedStableFilter = isRemovingStableFilter || isEditingStableFilter

		const newLogicOperator = nextFilterModel.logicOperator
		const computedValueItems = nextFilterModel.items.map((item) => {
			const isDayjsValue = isDayjsObject(item.value)
			if (!isDayjsValue) return item

			const momentObj = dayjsToMoment(item.value)
			return { ...item, value: momentObj.toISOString() }
		})

		const computedNextFilterModel = cloneDeep(nextFilterModel)
		computedNextFilterModel.items = computedValueItems
		// onHeaderFilterModel('filterModel', nextClientFilters)
		setGridFilterModel(computedNextFilterModel)
	}, 500)

	const handleColumnVisibilityModelChange = (newModel) => {
		setGridColumnVisibilityModel(newModel)
	}

	const hasAggregationModel = useMemo(() => !isEmpty(gridAggregationModel), [gridAggregationModel])

	return (
		<>
			<Box
				sx={{
					position: 'absolute',
					left: 0,
					right: 0,
					top: 30,
					bottom: 0,
					background: '#fff',

					'& .MuiDataGrid-selectedRowCount': {
						opacity: '0 !important',
					},
				}}
			>
				<ToastAlert alertInfo={alertInfo} setAlertInfo={setAlertInfo} />
				<DataGridPremium
					sx={{
						'& .MuiDataGrid-columnHeaders': {
							display: isShowHeaders ? 'revert' : 'none !important',

							'& .MuiDataGrid-columnHeaderDraggableContainer': {
								'& .MuiDataGrid-columnHeaderTitleContainer': {
									'& .MuiDataGrid-columnHeaderTitleContainerContent': {
										'& .MuiDataGrid-aggregationColumnHeaderLabel ': {
											position: hasAggregationModel ? 'relative' : 'absolute',
										},
									},
								},
							},
						},
						'& .MuiDataGrid-footerContainer': {
							minHeight: '40px !important',
							display: isShowTotalRows ? 'revert' : 'none !important',
						},
						...bgColorRowStyles,
					}}
					localeText={{
						// Export selector toolbar button text
						toolbarExport: t('common:misc.export'),
						toolbarExportLabel: t('common:misc.exportLabel'),
						toolbarExportCSV: t('common:misc.exportCSV'),
						toolbarExportPrint: t('common:misc.exportPrint'),
						toolbarExportExcel: t('common:misc.exportExcel'),
					}}
					getRowClassName={(params) => {
						const bgColor = getRowBackgroundColor(params)
						const newBgColor = bgColor ? bgColor.substring(1) : ''
						return bgColor ? `app-row-datagrid-${newBgColor}` : ''
					}}
					rows={formattedRowsWithNumbers}
					columns={neededColumns}
					columnVisibilityModel={gridColumnVisibilityModel}
					onColumnVisibilityModelChange={handleColumnVisibilityModelChange}
					onColumnResize={(params, event, details) => {
						const colsWidth = columnsWidth
						colsWidth[params.colDef.field] = params.colDef.width
						setColumnsWidth(colsWidth)
					}}
					apiRef={apiRef}
					slots={{
						footer: CustomFooter,
						toolbar: hasCustomToolbar ? CustomToolbar : null,
					}}
					slotProps={{
						filterPanel: {
							disableRemoveAllButton: isHideRemoveAllFilters,
						},
						toolbar: {
							apiRef,
							viewData,
							columns,
							rows,
							tab,
							recordsLoading,
							refetchRecords,
							haveImport,
							haveExport,
							haveRefresh,
							printTitle,
							records,
							gridAggregationModel,
							gridRowGroupingModel,
							gridSortModel,
							gridFilterModel,
							formattedRowsWithNumbers,
							hasMoreRecords,
							isLoading,
							showNewBtn,
							handleOpen,
							allowDispatch,
							setOpenSendDispatch,
						},
					}}
					rowHeight={25}
					columnHeaderHeight={28}
					filterModel={gridFilterModel}
					onFilterModelChange={handleFilterModelChange}
					onCellClick={(params) => {
						if (params.field === 'actions') {
							return
						}

						const selectedRowData = defaultRows.filter((row) => row.id === params.id)

						if (
							params.field === mainModalDashboardSetting?.dataField &&
							!parentModalDashboardSetting &&
							mainModalDashboardSetting
						) {
							const dataFieldValue = selectedRowData?.[0]?.[mainModalDashboardSetting.dataField]
							handleOpenModalDashboardDialog({
								passedIdValue: dataFieldValue,
								modalDashboardSetting: mainModalDashboardSetting,
							})
						}

						onSelectRow(tile?.key, selectedRowData?.[0])
						setSelectionModel((prevModel) => {
							if (prevModel[0] === params.id) {
								return []
							}
							return [params.id]
						})
						onSelectCurrent(tileName)
					}}
					// onRowClick={(params, event) => {
					// 	const selectedRowData = defaultRows.filter((row) => row.id === params.id)

					// 	// if (!parentModalDashboardSetting && mainModalDashboardSetting) {
					// 	// 	const dataFieldValue = selectedRowData?.[0]?.[mainModalDashboardSetting.dataField]
					// 	// 	handleOpenModalDashboardDialog({
					// 	// 		passedIdValue: dataFieldValue,
					// 	// 		modalDashboardSetting: mainModalDashboardSetting,
					// 	// 	})
					// 	// }

					// 	onSelectRow(tile?.key, selectedRowData?.[0])
					// 	setSelectionModel((prevModel) => {
					// 		if (prevModel[0] === params.id) {
					// 			return []
					// 		}
					// 		return [params.id]
					// 	})
					// 	onSelectCurrent(tileName)
					// }}
					rowSelectionModel={selectionModel}
					aggregationModel={gridAggregationModel}
					onAggregationModelChange={(newModel) => onHeaderFilterModel('aggregationModel', newModel)}
					rowGroupingModel={gridRowGroupingModel}
					onRowGroupingModelChange={(newModel) => onHeaderFilterModel('rowGroupingModel', newModel)}
					sortModel={gridSortModel}
					onSortModelChange={(newModel) => onHeaderFilterModel('sortModel', newModel)}
					getRowHeight={() => {
						if (dynamicRowHeight) return 'auto'
						return 25
					}}
				/>
			</Box>

			{action && (
				<DoformsRecordsActions
					formSelected={formSelected}
					setExternalError={setError}
					open={open}
					action={action}
					setAction={setAction}
					environment={environment}
					actionRecord={actionRecord}
					name={viewData.name}
					tab={tab}
					columns={columns}
					onClose={handleClose}
					refreshData={refetchRecords}
					setGridRows={setCurrentRecords}
				/>
			)}

			{allowDispatch && (
				<DatagridSendDispatch
					setOpenSendDispatch={setOpenSendDispatch}
					formSelected={formSelected}
					action={'send'}
					title={t('common:tabs.dispatchScheduler')}
					recordsLoading={false}
					source={'dashboard'}
					tab={'forms'}
					environment={environment}
					openSendDispatch={openSendDispatch}
					viewType={viewType}
				/>
			)}

			{itemTile && isOpenDialog === true && (
				<div>
					<Dialog
						fullWidth={true}
						maxWidth="md"
						aria-labelledby="transition-modal-title"
						aria-describedby="transition-modal-description"
						className={classes.dialog}
						sx={{
							'& .MuiDialog-paper': {
								height: '90% !important',
							},
						}}
						open={isOpenDialog}
						closeAfterTransition
					>
						<DialogTitle id="record-dialog-title">
							<div style={{ float: 'left', display: 'inline' }}>{titleLabel}</div>
							<div style={{ float: 'right', display: 'inline' }}>
								<IconButton onClick={(e) => handleCloseDialog(true)} alignItems="flex-end">
									<CloseIcon />
								</IconButton>
							</div>
						</DialogTitle>
						<DialogContent>
							<TileComponent
								tile={itemTile}
								setIsOpenDialog={() => {
									setIsOpenDialog(false)
								}}
							/>
						</DialogContent>
					</Dialog>
				</div>
			)}

			<ModalDashboard />
		</>
	)
}

const CustomToolbar = (props) => {
	const {
		apiRef,
		viewData,
		columns,
		rows,
		tab,
		recordsLoading,
		refetchRecords,
		haveImport,
		haveExport,
		haveRefresh,
		printTitle,
		records,
		gridAggregationModel,
		gridRowGroupingModel,
		gridSortModel,
		gridFilterModel,
		formattedRowsWithNumbers,
		hasMoreRecords,
		isLoading,
		showNewBtn,
		handleOpen,
		allowDispatch,
		setOpenSendDispatch,
	} = props

	const { environment } = useSelector((state) => state)
	const [t] = useTranslation('common')
	const iconTheme = environment.theme.icons
	const classes = useStyles(iconTheme)
	return (
		<GridToolbarContainer
			sx={{
				'& .MuiButton-root': {
					color: environment.iconsTheme[0],
					':hover': {
						color: environment.iconsTheme[1],
						backgroundColor: 'transparent',
					},
				},
				padding: '0 5px',
			}}
		>
			{haveImport &&
				!environment.isReadOnlySubmission &&
				SUPPORT_IMPORTED_VIEW_TYPES.includes(viewData.type) && (
					<DoformsDataImportView
						environment={environment}
						gridColumns={columns}
						viewData={viewData}
						queryView={null}
						tab={tab}
						recordsLoading={recordsLoading}
						formSelected={viewData}
						onHandleImportComplete={refetchRecords}
					/>
				)}
			{haveExport && (
				<ExportButton
					printTitle={printTitle}
					sx={environment.iconsTheme}
					viewData={viewData}
					gridRows={rows}
					gridColumns={columns}
					records={records}
					aggregationModel={gridAggregationModel}
					isGrouping={gridRowGroupingModel?.length > 0}
					formattedRowsWithNumbers={formattedRowsWithNumbers}
					formatGridColumns={columns}
					rowGroupingModel={gridRowGroupingModel}
					sortModel={gridSortModel}
					filterModel={gridFilterModel}
					getVisibleRows={() => {
						const paginationRowModels = gridPaginatedVisibleSortedGridRowEntriesSelector(apiRef)
						let rs = []
						paginationRowModels.forEach((rowModel, index) => {
							const a = rowModel.model
							// a.id = index + 1
							rs.push(a)
						})
						return rs
					}}
					hasMoreRecords={hasMoreRecords}
					dataLoading={isLoading || recordsLoading}
				/>
			)}
			{showNewBtn === true && (
				<Tooltip title={`${t('tooltip.prefix.new')}`} placement="bottom-start" disableInteractive>
					<LoadingButton
						id={'create-submission'}
						sx={environment.iconsTheme}
						//className={classes.icon}
						disabled={false}
						loadingPosition="start"
						onClick={(e) => handleOpen(e)}
						startIcon={<AddIcon />}
					>
						{t('common:misc.new')}
					</LoadingButton>
				</Tooltip>
			)}
			{!environment.isReadOnlyDispatch && allowDispatch && (
				<div>
					<LoadingButton
						id={'send-dispatch'}
						className={classes.icon}
						loadingPosition="start"
						onClick={(e) => {
							setOpenSendDispatch(true)
						}}
						startIcon={<ShortcutIcon />}
					>
						{t('common:filters.dispatch')}
					</LoadingButton>
				</div>
			)}
			{haveRefresh && (
				<div>
					<LoadingButton
						id={'refresh-data'}
						className={classes.icon}
						loadingPosition="start"
						onClick={(e) => {
							e.preventDefault()
							refetchRecords()
						}}
						startIcon={<RefreshIcon />}
					>
						{t('common:misc.refresh')}
					</LoadingButton>
				</div>
			)}
		</GridToolbarContainer>
	)
}

export default DataTable
